import React, { useState } from 'react';
import { Mail, User, MessageSquare, CheckCircle, Phone, MapPin, Clock } from 'lucide-react';
import { Meta } from '../components/Meta';
import { supabase } from '../lib/supabase';

export function Suggestions() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create notification for all admins
      const { data: admins } = await supabase
        .from('users')
        .select('id')
        .eq('is_admin', true);

      if (admins && admins.length > 0) {
        const notifications = admins.map(admin => ({
          user_id: admin.id,
          type: 'contact',
          title: 'New Contact Form Message',
          message: `${formData.name}: ${formData.subject}`,
          data: {
            sender_name: formData.name,
            sender_email: formData.email,
            subject: formData.subject,
            message: formData.message,
            submitted_at: new Date().toISOString()
          }
        }));

        const { error: notificationError } = await supabase
          .from('notifications')
          .insert(notifications);

        if (notificationError) throw notificationError;
      }

      setShowSuccess(true);

      setTimeout(() => {
        setFormData({ name: '', email: '', subject: '', message: '' });
        setShowSuccess(false);
      }, 3000);
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F3F7EE] to-white flex items-center justify-center">
        <Meta
          title="Message Sent | Mindful Family"
          description="Thank you for contacting us! We'll get back to you soon."
        />

        <div className="max-w-md mx-auto px-4 text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>

            <h1 className="text-2xl font-gelica font-bold text-content mb-4">
              Message Sent!
            </h1>

            <p className="text-content/70 mb-6">
              Thank you for reaching out. Our team will review your message and get back to you within 24-48 hours.
            </p>

            <div className="inline-flex items-center px-4 py-2 bg-accent-base/10 text-accent-text rounded-full text-sm">
              <Mail className="h-4 w-4 mr-2" />
              We'll respond to {formData.email}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F3F7EE] to-white">
      <Meta
        title="Contact Us | Mindful Family"
        description="Get in touch with the Mindful Family team. We're here to help with any questions or feedback."
      />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-text/10 rounded-full mb-6">
              <Mail className="h-8 w-8 text-accent-text" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-gelica text-content leading-tight mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-content/70 max-w-2xl mx-auto">
              Have a question or feedback? We'd love to hear from you.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-gelica font-bold text-content mb-4">
                  Get in Touch
                </h3>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent-text/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-accent-text" />
                    </div>
                    <div>
                      <p className="text-sm text-content/60">Email</p>
                      <p className="text-content font-medium">support@mindful.family</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent-text/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="h-5 w-5 text-accent-text" />
                    </div>
                    <div>
                      <p className="text-sm text-content/60">Response Time</p>
                      <p className="text-content font-medium">24-48 hours</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent-text/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="h-5 w-5 text-accent-text" />
                    </div>
                    <div>
                      <p className="text-sm text-content/60">General Inquiries</p>
                      <p className="text-content font-medium">Questions about the platform</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-gelica font-bold text-content mb-4">
                  Popular Topics
                </h3>
                <div className="space-y-2">
                  {['Account Issues', 'Partnerships', 'Feature Requests', 'Billing Questions', 'Content Removal'].map((topic) => (
                    <button
                      key={topic}
                      onClick={() => setFormData(prev => ({ ...prev, subject: topic }))}
                      className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-colors ${
                        formData.subject === topic
                          ? 'bg-accent-text text-white'
                          : 'bg-accent-base/10 text-content/70 hover:bg-accent-base/20'
                      }`}
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-xl font-gelica font-bold text-content mb-6">
                  Send us a Message
                </h3>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-content mb-2">
                        Your Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-content/40" />
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-accent-text focus:ring-1 focus:ring-accent-text/20 bg-gray-50"
                          placeholder="John Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-content mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-content/40" />
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-accent-text focus:ring-1 focus:ring-accent-text/20 bg-gray-50"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-content mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-accent-text focus:ring-1 focus:ring-accent-text/20 bg-gray-50"
                      placeholder="How can we help you?"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-content mb-2">
                      Message *
                    </label>
                    <textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-accent-text focus:ring-1 focus:ring-accent-text/20 bg-gray-50"
                      rows={6}
                      placeholder="Please describe your question or feedback in detail..."
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <p className="text-red-600 text-sm">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full flex items-center justify-center px-8 py-4 text-lg font-medium rounded-xl text-white transition-all duration-300 ${
                      loading
                        ? 'bg-accent-text/70 cursor-not-allowed'
                        : 'bg-accent-text hover:bg-accent-text/90 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="h-5 w-5 mr-3" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
